import { Button, Input, Result, Form } from 'antd'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@tanstack/react-query'
import { contextCompanyIdSelector } from '../../../../../../redux/context/company/selectors'
import { notificationAction } from '../../../../../../redux/middleware/actions'
import { AppDispatch } from '../../../../../../redux/store'
import { integrationClient } from '../../../../../../services/api/integrationClient'
import DataUpdate from '../../dataUpdate'

const SimphonyApi: React.FC = () => {
  const { t } = useTranslation()
  const companyId = useSelector(contextCompanyIdSelector)
  const dispatch: AppDispatch = useDispatch()

  const [form] = Form.useForm()
  const [showForm, setShowForm] = React.useState(false)

  const setCredentialsMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await integrationClient.put(`/simphony/authentication/${companyId}`, data)
      return response.data
    }
  })

  React.useEffect(() => {
    setCredentialsMutation.reset()
    setShowForm(false)
  }, [companyId])

  const { data: credentials } = useQuery({
    enabled: !showForm,
    queryKey: ['simphony-authentication', companyId],
    queryFn: async () => {
      const response = await integrationClient.get(`/simphony/authentication/${companyId}`)
      return response.data
    },
    retry: false
  })

  const authorized = !showForm && (credentials?.httpCode === 200 || setCredentialsMutation.data?.httpCode === 200)

  const submit = () => {
    form
      .validateFields()
      .then(data => {
        setCredentialsMutation.mutate(data, {
          onSuccess: () => {
            setShowForm(false)
            dispatch(
              notificationAction({
                type: 'success',
                message: 'AUTHENTICATION_SUCCESS',
                description: 'IMPORT_SUCCESS_EMAIL'
              })
            )
          },
          onError: () => {
            dispatch(
              notificationAction({
                type: 'error',
                message: 'AUTHENTICATION_ERROR',
                description: 'USERNAME_PASSWORD_MISMATCH'
              })
            )
          }
        })
      })
      .catch(() => undefined)
  }

  const emptyCredentials = () => {
    form.resetFields()
    setCredentialsMutation.reset()
    setShowForm(true)
  }

  if (authorized) {
    return (
      <>
        <Result
          status="success"
          title={t('integrationsPage:authenticated')}
          extra={
            <>
              <Button onClick={emptyCredentials} type="primary">
                {t('integrationsPage:authenticate')}
              </Button>
              <DataUpdate page="simphony" />
            </>
          }
        />
      </>
    )
  }

  return (
    <Form form={form} layout="vertical">
      <Form.Item
        style={{ marginTop: 10 }}
        label="username"
        name="username"
        rules={[{ required: true, message: t('global:required-field') }]}
      >
        <Input />
      </Form.Item>
      <Form.Item label="password" name="password" rules={[{ required: true, message: t('global:required-field') }]}>
        <Input.Password />
      </Form.Item>
      <Form.Item label="orgName" name="orgName" rules={[{ required: true, message: t('global:required-field') }]}>
        <Input />
      </Form.Item>
      <Form.Item label="clientId" name="clientId" rules={[{ required: true, message: t('global:required-field') }]}>
        <Input />
      </Form.Item>
      <Form.Item label="baseUrl" name="baseUrl" rules={[{ required: true, message: t('global:required-field') }]}>
        <Input />
      </Form.Item>
      <Form.Item label="oidcUrl" name="oidcUrl" rules={[{ required: true, message: t('global:required-field') }]}>
        <Input />
      </Form.Item>
      <Form.Item label="orgIdentifier" name="orgIdentifier">
        <Input />
      </Form.Item>
      <Form.Item>
        <Button type="primary" onClick={() => submit()} loading={setCredentialsMutation.isPending}>
          {t('integrationsPage:authenticate')}
        </Button>
      </Form.Item>
    </Form>
  )
}

export default SimphonyApi
